import { Store } from "./store";
import i18n, { aliases } from "../i18n/i18n";
import { fetcher } from "@weezevent/weezjs-core";
import moment from "moment/moment";
import { useSyncExternalStore } from "hooks/useSyncExternalStore";

export class LocaleStore extends Store {
	/**
	 * Updates the application's language and locale settings based on the user's selected locale.
	 *
	 * - If `userLocale` is not provided, the function exits early.
	 * - Replaces underscores (`_`) with hyphens (`-`) to standardize the locale format.
	 * - Checks if the locale has an alias and retrieves the corresponding value; otherwise, it keeps the original.
	 * - Updates `i18n.accountLocale` with the standardized locale.
	 * - If the new locale differs from the current language in `i18n`, it:
	 *   - Changes the application language using `i18n.changeLanguage(locale)`.
	 *   - Updates the fetcher instance with the new language setting.
	 *
	 * This ensures that both the UI language and API requests reflect the selected locale.
	 *
	 * @param {string} userLocale - The user's selected locale (e.g., "en_US", "fr_FR").
	 */
	setUserLocale = userLocale => {
		if (!userLocale) {
			return;
		}

		const accountsTokenLocale = userLocale.replace(/_/g, "-");
		const locale = aliases[accountsTokenLocale] ?? accountsTokenLocale;
		i18n.accountLocale = accountsTokenLocale;

		if (locale !== i18n.language) {
			i18n.changeLanguage(locale);
			fetcher.setLanguage(locale);
			moment.locale(locale);
		}

		this.setState(locale);
	};
}

export const localeStore = new LocaleStore();

export const useLocaleStore = () =>
	useSyncExternalStore(localeStore.subscribe, () => localeStore.state);
